/**
 * Minimal page for headless social card capture (Playwright). Same card UI as admin
 * Social Media Mgmt; data from signed ``/api/social-export/bootstrap``.
 *
 * Query: ``?token=<base64url>`` (token embeds date + pick_type).
 * When ready, sets ``document.documentElement.dataset.exportReady = '1'`` for automation.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  PicksOfTheDayCard,
  ParlayOfTheDayCard,
  CARD_WIDTH_PX,
} from './AdminSocialCardsPage';

export default function SocialCardExportPage() {
  const [searchParams] = useSearchParams();
  const token = (searchParams.get('token') || '').trim();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pickType, setPickType] = useState(null);
  const [dateStr, setDateStr] = useState(null);
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    document.documentElement.removeAttribute('data-export-ready');
  }, []);

  useEffect(() => {
    if (!token) {
      setError('Missing token');
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    // Path only: axios.defaults.baseURL (App.js) already includes /api — do not prefix again
    // or the request becomes /api/api/social-export/bootstrap.
    axios
      .get('/social-export/bootstrap', { params: { token } })
      .then((res) => {
        if (cancelled) return;
        const pt = res.data?.pick_type;
        const d = res.data?.date;
        const pl = res.data?.payload;
        if (!pt || !d) {
          setError('Invalid bootstrap response');
          return;
        }
        setPickType(pt);
        setDateStr(d);
        setPayload(pl);
      })
      .catch((err) => {
        if (cancelled) return;
        const msg =
          err.response?.data?.error ||
          err.message ||
          'Failed to load card data';
        setError(String(msg));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (!payload || !pickType) return undefined;

    // SHAP charts need a beat after paint; wait longer when insights are present.
    const picks = payload?.picks_of_the_day || [];
    const hasShap = picks.some((p) => p?.shap?.feature_names?.length > 0);
    const delayMs = hasShap ? 2200 : 1200;

    const markReady = () => {
      document.documentElement.setAttribute('data-export-ready', '1');
    };

    const id = window.setTimeout(markReady, delayMs);
    return () => {
      window.clearTimeout(id);
      document.documentElement.removeAttribute('data-export-ready');
    };
  }, [payload, pickType]);

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#1a365d',
          color: '#fff',
          fontFamily: 'system-ui, sans-serif',
        }}
      >
        Loading…
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#1a365d',
          color: '#fed7d7',
          padding: 24,
          fontFamily: 'system-ui, sans-serif',
        }}
      >
        {error}
      </div>
    );
  }

  // Outer slate is for the browser only. Screenshot target is the card itself so
  // Twitter/Discord do not crop a large empty frame around a tiny card.
  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#0f172a',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: `${CARD_WIDTH_PX * 0.06}px`,
        boxSizing: 'border-box',
      }}
    >
      <div data-social-export-card="1">
        {pickType === 'picks_of_the_day' && (
          <PicksOfTheDayCard dateStr={dateStr} data={payload} cardRef={null} />
        )}
        {pickType === 'parlay_of_the_day' && (
          <ParlayOfTheDayCard dateStr={dateStr} data={payload} cardRef={null} />
        )}
      </div>
    </div>
  );
}
